import { HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import debounce from 'lodash/debounce';
import {
  MarketActivityIndicator,
  MarketDropdown,
  MarketInputText,
  MarketList,
  MarketPopover,
  MarketRow,
} from '@market/react';
import useAddressSuggestions, {
  USER_INPUT_VALUE,
} from 'typescript/hooks/use_address_suggestions_client';
import {
  AddressType,
  getAddressPrefix,
} from 'typescript/schemas/address_forms/address_fields';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { CountryCode } from 'typescript/constants/localization';
import { fixGBStateDetails } from 'typescript/utilities/address_utils';
import { DATADOG_PRIVACY_LEVELS } from 'typescript/constants/enums/datadog';
import styles from 'typescript/apps/checkout_v3/components/address_form/address_suggestions.module.scss';

const DROPDOWN_ID = 'address-suggestion-dropdown-id';
export const ADDRESS_SUGGESTION_TEST_ID = 'address-suggestion-test-id';

interface AddressSuggestionsProps extends HTMLAttributes<HTMLElement> {
  addressType?: AddressType;
}

const AddressSuggestions = ({
  className,
  addressType = AddressType.shipping,
}: AddressSuggestionsProps) => {
  const { t } = useTranslation();
  const { orderNumber } = useCheckoutFlow();
  const { setValue } = useFormContext();
  const { countryCode } = useLocaleInfo();
  const [searchInput, setSearchInput] = useState('');
  const { useGetAddressSuggestions, useGetSuggestionDetails } =
    useAddressSuggestions(orderNumber);
  const {
    suggestions,
    getAddressSuggestions,
    resetSuggestions,
    isPending: isLoading,
  } = useGetAddressSuggestions();
  const { getSuggestionDetails } = useGetSuggestionDetails();
  const prefix = useMemo(() => getAddressPrefix(addressType), [addressType]);
  const debouncedResetSuggestions = useMemo(
    () => debounce(resetSuggestions, 100),
    [],
  );
  const debouncedGetSuggestions = useMemo(
    () => debounce(getAddressSuggestions, 500),
    [],
  );

  const handleAddressInputChange = (event) => {
    const partialAddress = event?.detail?.value;
    // we need to set the search input here so that the user input is displayed and selectable
    setSearchInput(partialAddress);

    // reset the suggestions if the input is less than 3 characters
    if (partialAddress.length <= 3 && suggestions) {
      debouncedResetSuggestions();
    }

    // fetch suggestions if the input is more than 3 characters
    if (partialAddress.length > 3) {
      debouncedGetSuggestions(partialAddress);
    }
  };

  // if the user selects the first option or they click out of the popover,
  // we need to set the address line 1 to be the user input so the rest of the form shows up
  const setAddressLine1ToBeUserInput = () =>
    setValue(`${prefix}address_line_1`, searchInput, {
      shouldValidate: true,
    });

  const handleSuggestionSelect = (placeId: string) => {
    // if the user input is selected, we don't need to fetch the details
    if (placeId === USER_INPUT_VALUE) {
      setAddressLine1ToBeUserInput();
      return;
    }

    getSuggestionDetails(
      { placeId },
      {
        onSuccess: (suggestionDetails) => {
          const address = { ...suggestionDetails['address'] };

          if (countryCode === CountryCode.GB) {
            fixGBStateDetails(address);
          }

          Object.entries(address).forEach(([key, value]) => {
            setValue(`${prefix}${key}`, value, {
              shouldValidate: true,
            });
          });
        },
        onError: (error) => {
          // TODO: add error handling / logging
          console.error('Error fetching suggestion details', error);
        },
      },
    );
  };

  // MarketInputText doesn't trigger when browser autofill is used, so we need a way to manually trigger the dropdown
  // the hack is to target the dropdown and add the expanded attributes when the search input is more than 3 characters
  useEffect(() => {
    const dropdown = document.getElementById(
      DROPDOWN_ID,
    ) as HTMLMarketDropdownElement;

    if (searchInput.length > 3 && dropdown) {
      dropdown.setAttribute('expanded', '');
      dropdown.setAttribute('aria-expanded', '');
    }
  }, [searchInput]);

  return (
    <MarketDropdown
      className={className}
      id={DROPDOWN_ID}
      data-dd-privacy={DATADOG_PRIVACY_LEVELS.HIDDEN}
      popoverPlacement="bottom-start"
      onMarketDropdownClosed={setAddressLine1ToBeUserInput}
    >
      <MarketInputText
        data-testid={ADDRESS_SUGGESTION_TEST_ID}
        onMarketInputValueChange={(e) => handleAddressInputChange(e)}
        slot="trigger"
      >
        <label>{t('checkout.delivery.addressLabel.addressLine1')}</label>
      </MarketInputText>
      {searchInput.length > 0 && (
        <MarketPopover className={styles.popover} slot="popover">
          <MarketList
            onMarketListSelectionsDidChange={(e) =>
              handleSuggestionSelect(e.detail.newSelectionValue)
            }
          >
            <MarketRow value={USER_INPUT_VALUE}>
              {searchInput}
              {isLoading && (
                <MarketActivityIndicator size="small" slot="side-label" />
              )}
            </MarketRow>
            {suggestions?.map((suggestion) => (
              <MarketRow key={suggestion.place_id} value={suggestion.place_id}>
                {suggestion.combined_address}
              </MarketRow>
            ))}
          </MarketList>
        </MarketPopover>
      )}
    </MarketDropdown>
  );
};

export default AddressSuggestions;
